import { getLedger, type LedgerRow, type LedgerTotals } from '../api';
import { useAsync } from '../lib/async';
import { Empty, Result } from '../lib/ui';

/**
 * The money, across every family.
 *
 * The ledger existed per-family only, which answers "what happened to this
 * account" and never "what is happening to the business". Grants, holds,
 * releases and waived spend on one page is the difference.
 *
 * `waived` rows are the internal/unlimited accounts: no credits moved, but real
 * provider money did. They are shown separately rather than folded in, because
 * counting internal testing as though it were revenue is how a dashboard
 * starts lying to you.
 *
 * WHAT THIS SCREEN GOT WRONG, twice, and on the one screen where it matters
 * most. It held `useState<LedgerRow[]>([])` with no loading state and a bare
 * `error` string, so:
 *
 *   * every single visit painted the header and the words "No movements yet."
 *     before the response arrived. On the money screen. An operator who looked
 *     away for the half second the scan takes read "this business has moved no
 *     credits" — the empty-table lie, told by the table that must never tell
 *     it. `Result` cannot express it: loading and empty are different states of
 *     a discriminated union, so no render can be in both.
 *   * `if (error) return <p className="warn">{error}</p>` made the ENTIRE screen
 *     one line of amber text. For the bodyless Kestrel 404 that this
 *     deployment's own test pins — ApiError.message is the literal string
 *     "404" — the Money screen became the word 404. A mid-session role removal
 *     became an amber line rather than the sentence explaining that the account
 *     is not in the staff role, and neither told the operator whether any
 *     credits had moved.
 *
 * `Result` runs the three discriminators (missing route, permanently
 * unavailable, not staff) that this screen had no access to at all.
 */
export function Ledger() {
  const { result, reload } = useAsync(() => getLedger(), []);

  return (
    <section>
      <h2>Money</h2>

      <Result of={result} what="the ledger" retry={reload}>
        {(page) => (
          <>
            <Totals totals={page.totals} />
            {!page.entries.length ? (
              // "The call succeeded and came back empty" — which on this screen
              // is a real and reportable fact, as opposed to the same words
              // printed while the request was still in flight.
              <Empty what="movements" />
            ) : (
              <Movements rows={page.entries} />
            )}
          </>
        )}
      </Result>
    </section>
  );
}

function Totals({ totals }: { totals?: LedgerTotals }) {
  if (!totals) return null;
  return (
    <div className="cards">
      <Card label="Credits granted" value={totals.granted} />
      <Card label="Credits held" value={totals.held} />
      <Card label="Credits released" value={totals.released} />
      <Card label="Outstanding" value={totals.outstanding}
            hint="what families could still spend" />
      <Card label="Waived spend"
            value={`$${totals.waivedCostUsd.toFixed(2)}`}
            hint="real provider cost on unlimited accounts" />
    </div>
  );
}

function Movements({ rows }: { rows: LedgerRow[] }) {
  return (
    <table>
      <thead>
        <tr>
          <th>When</th><th>Family</th><th>What</th>
          <th className="num">Credits</th><th>Why</th><th>Who</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.familyId}-${row.entryId}`}>
            <td>{(row.at ?? '').replace('T', ' ').slice(0, 16)}</td>
            <td><code>{(row.familyId ?? '').slice(0, 8)}</code></td>
            <td>
              {row.type}
              {row.waived && <span className="pill"> waived</span>}
            </td>
            <td className={`num ${row.credits < 0 ? 'down' : 'up'}`}>
              {row.waived && row.wouldHaveCost
                ? `$${row.wouldHaveCost.toFixed(2)}`
                : row.credits}
            </td>
            <td>{row.reason ?? ''}</td>
            <td>{row.actor ?? ''}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Card({ label, value, hint }:
              { label: string; value: number | string; hint?: string }) {
  return (
    <div className="card">
      <b>{value}</b>
      <span>{label}</span>
      {hint && <em>{hint}</em>}
    </div>
  );
}
